/**
 * VideoPanel — HTML5 video player synchronized with the telemetry trace.
 *
 * Syncing contracts:
 *   • seekTo(seconds)         — called from App.jsx on lap selection / trace hover
 *   • trackapp:chart-hover    — window event from SpeedTrace; payload has { videoTime }
 *   • trackapp:video-time     — dispatched with time, distance and transport state
 *                               so traces and the embedded 3D view can follow
 *
 * Exposed via forwardRef / useImperativeHandle so App.jsx can call seekTo().
 *
 * Split view:
 *   When 2+ laps are selected the user can enable a "split" view that renders one
 *   <video> pane per lap, all reading the same session file but seeked to each
 *   lap's position. One pane is the "lead" (its transport drives the others); the
 *   followers track it either by track position (distance — default) or by
 *   lap-relative time. Per-lap distance↔time lookups are built from the lap traces.
 */
import {
  forwardRef, lazy, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef,
  useState,
} from 'react';
import Hls from 'hls.js';
import styles from './VideoPanel.module.css';
import {
  cancelVideoComparisonExport,
  downloadLapClip,
  getLapClipStatus,
  submitLapClip,
  createVideoComparisonExport,
  deleteSessionVideo,
  downloadVideoComparisonExport,
  getVideoComparisonExport,
} from '../../services/api';
import { lastCursor } from '../../utils/chartCursor';
import { shouldSeekFromChartHover } from '../../utils/chartSyncPolicy';
import { distanceMetersAtVideoTime } from '../../utils/videoPlaybackSync';
import {
  VIDEO_COMPARISON_TERMINAL,
  buildVideoComparisonExportRequest,
  comparison3dCellSize,
  comparison3dTileLaps,
} from '../../utils/videoComparisonExport';
import HelpButton from '../HelpButton/HelpButton';
// The probe lives in its own dependency-free module: importing it from
// exportVideo would reach hudCanvas -> scene and pull ~800 kB of three.js into
// the eager bundle. The capture itself stays behind the lazy boundary below.
import { canExportVideo } from '../Lap3D/canvasCapture';

const Comparison3DCapture = lazy(() => import('./Comparison3DCapture'));

const M_TO_FT = 3.28084;

function fmt(sec) {
  if (sec == null || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Linear-interpolated lookup over an array sorted ascending by xKey.
// Returns the yKey value at x, clamping at the ends. null for empty input.
function lerpLookup(arr, xKey, yKey, x) {
  const n = arr.length;
  if (!n) return null;
  if (x <= arr[0][xKey])     return arr[0][yKey];
  if (x >= arr[n - 1][xKey]) return arr[n - 1][yKey];
  let lo = 0, hi = n - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid][xKey] <= x) lo = mid; else hi = mid;
  }
  const a = arr[lo], b = arr[hi];
  const span = b[xKey] - a[xKey];
  const frac = span > 0 ? (x - a[xKey]) / span : 0;
  return a[yKey] + frac * (b[yKey] - a[yKey]);
}

// Detect an HLS playlist URL. Tolerates a query string — the backend appends a
// `?v=<render_job_id>` cache-buster to hls_url (see sessions.py), so a plain
// `.endsWith('.m3u8')` misses it and the playlist gets assigned to video.src as a
// native source, which Firefox can't decode (no native HLS) → blank video.
// Mirrors the isHls helper in services/videoCache.js.
const isHlsUrl = (url) => /\.m3u8(\?|$)/i.test(url);

// ── A single <video> element that owns its own HLS attach lifecycle. ─────────
// Multiple panes can point at the same .m3u8 — each needs its own Hls instance,
// so the attach logic lives here rather than once at the panel level.
function LapVideo({ src, sessionId, className, onEl, handlers, splitPane = false }) {
  const elRef  = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const v = elRef.current;
    if (!v) return;
    const source = src ?? `/api/video/${sessionId}`;

    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    if (isHlsUrl(source)) {
      if (Hls.isSupported()) {
        // ABR-aware config. With a multi-rendition (ABR) master playlist this lets
        // hls.js adapt per pane; capLevelToPlayerSize makes small split panes pull
        // a low rendition (huge bandwidth win when several play at once). Tighter
        // buffers keep memory sane with N concurrent instances and lower the
        // simultaneous bandwidth demand even on today's single-rendition HLS.
        const hls = new Hls({
          capLevelToPlayerSize: true,
          maxBufferLength:    splitPane ? 12 : 30,
          maxMaxBufferLength: splitPane ? 30 : 120,
          backBufferLength:   splitPane ? 10 : 30,
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR: hls.startLoad(); break;
            case Hls.ErrorTypes.MEDIA_ERROR:   hls.recoverMediaError(); break;
            default:                           hls.destroy(); break;
          }
        });
        hls.loadSource(source);
        hls.attachMedia(v);
        hlsRef.current = hls;
      } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = source;
      }
    } else {
      v.src = source;
    }

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [src, sessionId, splitPane]);

  // Register/unregister the DOM element with the parent.
  useEffect(() => {
    onEl?.(elRef.current);
    return () => onEl?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <video
      ref={elRef}
      className={className}
      preload="auto"
      playsInline
      crossOrigin="anonymous"
      {...handlers}
    />
  );
}

const VideoPanel = forwardRef(function VideoPanel(
  { sessionId, videoSrc, syncOffset = 0, defaultTrim = 0, downloadUrl, onClose, onVideoDeleted,
    laps = [], selectedLaps = [], lapColors = {}, lapTraces = {},
    videoDrivers = null,
    // What the 3D panel is racing right now — every loaded driver's ticked
    // laps through pickLap3dLaps, not just the drivers with video. The export's
    // ghost-race tile fills from it so it shows the same cars the panel does.
    lap3dLaps = null,
    // ── Driver 2 (two-driver video). Present only when BOTH drivers have video. ──
    videoSrc2 = null, sessionId2 = null, defaultTrim2 = 0,
    laps2 = [], selectedLaps2 = [], lapColors2 = {}, lapTraces2 = {},
    driverName1 = 'Driver 1', driverName2 = 'Driver 2' },
  ref,
) {
  const videoRef     = useRef(null);   // the active / lead <video> element
  const progressRef  = useRef(null);
  const [playing,    setPlaying]    = useState(false);
  const [currentT,   setCurrentT]  = useState(0);
  const [duration,   setDuration]  = useState(0);
  const [seeking,    setSeeking]    = useState(false);   // true while scrubbing the bar
  const [trim,       setTrim]       = useState(defaultTrim);  // client-side offset adjustment (seconds)
  const ignoreHoverRef = useRef(false);  // suppress hover-sync while user scrubs
  const [syncOnMouseMove, setSyncOnMouseMove] = useState(
    () => localStorage.getItem('videoPanel.syncOnMouseMove') === 'true'
  );
  const syncOnMouseMoveRef = useRef(
    localStorage.getItem('videoPanel.syncOnMouseMove') === 'true'
  );
  const [clipOpen,    setClipOpen]    = useState(false);
  const [clipLapIdx,  setClipLapIdx]  = useState(null);
  // Last lap of the span (inclusive). Kept >= clipLapIdx by the handlers, so
  // the pair can only ever name consecutive laps.
  const [clipEndIdx,  setClipEndIdx]  = useState(null);
  const [clipPre,     setClipPre]     = useState(5);
  const [clipPost,    setClipPost]    = useState(5);
  const [clipLoading, setClipLoading] = useState(false);
  // The in-flight (or just-finished) clip job, as last polled.
  const [clipJob,     setClipJob]     = useState(null);
  const [clipError,   setClipError]   = useState(null);
  const [compareExportOpen, setCompareExportOpen] = useState(false);
  const [compareExportMode, setCompareExportMode] = useState('distance');
  const [compareExportPre, setCompareExportPre] = useState(5);
  const [compareExportPost, setCompareExportPost] = useState(5);
  const [compareExportJob, setCompareExportJob] = useState(null);
  const [compareExportError, setCompareExportError] = useState(null);
  const [compareExportBusy, setCompareExportBusy] = useState(false);
  const [compareExport3D, setCompareExport3D] = useState(false);
  // Set while the ghost race is being rendered in the browser; holds the exact
  // lap set handed to the capture so it cannot drift under it mid-render.
  const [capture3D, setCapture3D] = useState(null);
  const capture3DDone = useRef(null);
  const canRender3D = canExportVideo();

  // ── Split view state ─────────────────────────────────────────────────────
  const [split,    setSplit]    = useState(() => localStorage.getItem('videoPanel.split') === 'true');
  const [syncMode, setSyncMode] = useState(() => localStorage.getItem('videoPanel.splitSyncMode') || 'distance');
  // 'sync' = reference pane drives the others (they stay aligned); 'race' = every
  // pane plays its own lap in real time (ghost race, positions drift apart).
  const [playMode, setPlayMode] = useState(() => localStorage.getItem('videoPanel.splitPlayMode') || 'sync');
  const [leadLap,  setLeadLap]  = useState(null);   // index of the reference (REF) lap

  // Reset trim to the session default whenever the session changes
  useEffect(() => { setTrim(defaultTrim); }, [defaultTrim]);

  const effectiveSyncOffset = syncOffset + trim;

  // ── Lap views ─────────────────────────────────────────────────────────────
  // Real selected laps (exclude Theoretical Best, index -1).
  const visibleLaps = useMemo(
    () => laps.filter(l => l.index !== -1 && selectedLaps.includes(l.index)),
    [laps, selectedLaps],
  );
  const visibleLapIdx = useMemo(() => visibleLaps.map(l => l.index), [visibleLaps]);
  // Multi-session video is active when at least two usable driver descriptors exist.
  const dualDriver = videoDrivers?.length >= 2 || !!(videoSrc2 && laps2.length);
  // Single-driver lap split is mutually exclusive with two-driver mode.
  const splitActive = split && visibleLaps.length >= 2 && !dualDriver;

  // Absolute session start time per lap (works even when a trace isn't loaded).
  const lapStarts = useMemo(() => {
    const m = new Map();
    visibleLaps.forEach(l => m.set(l.index, l.start_time));
    return m;
  }, [visibleLaps]);

  // Per-lap distance↔time lookup tables, built from the lap traces.
  //   byTime: sorted by time-in-lap   → distance (m)
  //   byDist: sorted by distance (m)  → time-in-lap
  const lapLookup = useMemo(() => {
    const m = new Map();
    for (const lap of visibleLaps) {
      const pts = lapTraces[lap.index]?.points;
      if (!pts?.length) continue;
      const clean = pts
        .filter(p => p.time != null && p.dist_m != null)
        .map(p => ({ time: p.time, dist: p.dist_m }));
      if (!clean.length) continue;
      const byTime = [...clean].sort((a, b) => a.time - b.time);
      const byDist = [...clean].sort((a, b) => a.dist - b.dist);
      m.set(lap.index, { byTime, byDist });
    }
    return m;
  }, [visibleLaps, lapTraces]);

  const timedLapDisplayNum = useMemo(() => new Map(
    laps.filter(l => !l.is_outlap && !l.is_inlap && l.index !== -1).map((l, i) => [l.index, i + 1])
  ), [laps]);

  const driverInputs = useMemo(() => {
    if (videoDrivers?.length) return videoDrivers.slice(0, 4);
    const legacy = [{
      sessionId, videoSrc, defaultTrim, laps, selectedLaps, lapColors, lapTraces,
      name: driverName1,
    }];
    if (videoSrc2 && laps2.length) legacy.push({
      sessionId: sessionId2, videoSrc: videoSrc2, defaultTrim: defaultTrim2,
      laps: laps2, selectedLaps: selectedLaps2, lapColors: lapColors2,
      lapTraces: lapTraces2, name: driverName2,
    });
    return legacy;
  }, [videoDrivers, sessionId, videoSrc, defaultTrim, laps, selectedLaps, lapColors,
      lapTraces, driverName1, videoSrc2, laps2, sessionId2, defaultTrim2,
      selectedLaps2, lapColors2, lapTraces2, driverName2]);

  const driverData = useMemo(() => driverInputs.map((driver, index) => {
    const visible = (driver.laps || []).filter(
      lap => lap.index !== -1 && (driver.selectedLaps || []).includes(lap.index)
    );
    const starts = new Map(visible.map(lap => [lap.index, lap.start_time]));
    const lookup = new Map();
    for (const lap of visible) {
      const points = driver.lapTraces?.[lap.index]?.points || [];
      const clean = points
        .filter(point => point.time != null && point.dist_m != null)
        .map(point => ({ time: point.time, dist: point.dist_m }));
      if (!clean.length) continue;
      lookup.set(lap.index, {
        byTime: [...clean].sort((a, b) => a.time - b.time),
        byDist: [...clean].sort((a, b) => a.dist - b.dist),
      });
    }
    const timed = new Map((driver.laps || [])
      .filter(lap => !lap.is_outlap && !lap.is_inlap && lap.index !== -1)
      .map((lap, lapIndex) => [lap.index, lapIndex + 1]));
    return { ...driver, index, key: `d${index + 1}`, visible, starts, lookup, timed };
  }), [driverInputs]);

  // Focus lap per driver and the pane that owns transport/REF timing.
  const [focusLaps, setFocusLaps] = useState({});
  const [leadDriver, setLeadDriver] = useState(1);
  const [driverTrims, setDriverTrims] = useState({});
  const [liveDeltas, setLiveDeltas] = useState({});
  const lastDeltaUpdateRef = useRef(0);
  const driverSelectionKey = driverData
    .map(driver => `${driver.sessionId}:${driver.visible.map(lap => lap.index).join(',')}`)
    .join('|');
  useEffect(() => {
    setFocusLaps(previous => {
      const next = { ...previous };
      let changed = false;
      for (const driver of driverData) {
        if (!driver.visible.some(lap => lap.index === next[driver.sessionId])) {
          next[driver.sessionId] = driver.visible[0]?.index ?? null;
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [driverSelectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const driverDefaultsKey = driverData
    .map(driver => `${driver.sessionId}:${driver.defaultTrim || 0}`)
    .join('|');
  useEffect(() => {
    setDriverTrims(previous => {
      const next = { ...previous };
      for (const driver of driverData.slice(1)) {
        next[driver.sessionId] = Number(driver.defaultTrim) || 0;
      }
      return next;
    });
  }, [driverDefaultsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (leadDriver > driverData.length) setLeadDriver(1);
  }, [leadDriver, driverData.length]);

  const driverPanes = driverData.map((driver, index) => ({
    ...driver,
    focus: focusLaps[driver.sessionId] ?? null,
    setFocus: lapIndex => setFocusLaps(previous => ({
      ...previous, [driver.sessionId]: lapIndex,
    })),
    offset: index === 0
      ? effectiveSyncOffset
      : (driverTrims[driver.sessionId] ?? Number(driver.defaultTrim) ?? 0),
    colors: driver.lapColors || {},
    src: driver.videoSrc,
    sid: driver.sessionId,
  }));

  // A single video is exportable too, as long as the 3D view goes beside it —
  // on its own there is nothing to compare it to. `dualDriver` still gates the
  // split view, the live sync and the offsets row, which all genuinely need two.
  const soloWith3D = !dualDriver && driverPanes.length === 1 && canRender3D;
  const canCompareExport = dualDriver || soloWith3D;
  // Forced on when it is the only reason the export exists.
  const use3D = canRender3D && (compareExport3D || soloWith3D);

  // Which laps the ghost-race tile races: the video panes' laps first, then
  // whatever the 3D panel is showing (see comparison3dTileLaps).
  const lap3dLapsKey = (lap3dLaps || []).map(l => `${l.session_id}:${l.lap_index}`).join(',');
  const comparison3DLaps = useCallback((panes) => {
    const lapOf = (driver, lapIndex) => ({
      session_id: driver.sid,
      lap_index: lapIndex,
      name: driver.name,
      car: driver.session?.meta?.vehicle,
      color: driver.colors?.[lapIndex],
    });
    const own = panes.length === 1
      ? panes[0].visible.map(lap => lapOf(panes[0], lap.index))
      : [];
    return comparison3dTileLaps(panes.map(driver => lapOf(driver, driver.focus)), lap3dLaps || [], own);
    // lap3dLaps is rebuilt every render by the parent; the key is its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lap3dLapsKey]);

  const solo3DLapCount = soloWith3D ? comparison3DLaps(driverPanes).length : 0;
  const threeDNote = !canRender3D
    ? 'This browser cannot record the 3D view.'
    : soloWith3D
      ? (solo3DLapCount > 1
        ? `Beside the video, racing the ${solo3DLapCount} laps the 3D view shows. `
          + 'It renders here first, so keep the tab open.'
        : 'Beside the video. It renders here first, so keep the tab open.')
      : 'Adds the ghost race as a tile. It renders here first, so keep the tab open.';

  const compareExportLabel = soloWith3D
    ? 'Export the displayed lap beside the 3D view as an MP4'
    : `Export the ${driverPanes.length} displayed laps as a comparison MP4`;

  const compareSessionKey = driverPanes.map(driver => driver.sessionId).join('.');
  const compareExportStorageKey = useMemo(
    () => canCompareExport
      ? `videoPanel.compareExport.${compareSessionKey}`
      : null,
    [canCompareExport, compareSessionKey],
  );

  // Recover a render if the VIDEO panel was closed/reopened while VTR kept working.
  useEffect(() => {
    if (!canCompareExport || !compareExportStorageKey) {
      setCompareExportJob(null);
      return;
    }
    const exportId = localStorage.getItem(compareExportStorageKey);
    if (!exportId) {
      setCompareExportJob(null);
      return;
    }
    getVideoComparisonExport(exportId)
      .then(setCompareExportJob)
      .catch(() => {
        localStorage.removeItem(compareExportStorageKey);
        setCompareExportJob(null);
      });
  }, [canCompareExport, compareExportStorageKey]);

  // Poll only while the job is non-terminal. Completed exports remain available
  // until their backend expiry, so reopening the panel restores Download.
  useEffect(() => {
    const exportId = compareExportJob?.export_id;
    const status = compareExportJob?.status;
    if (!exportId || VIDEO_COMPARISON_TERMINAL.has(status)) return;
    let stopped = false;
    const poll = async () => {
      try {
        const next = await getVideoComparisonExport(exportId);
        if (!stopped) setCompareExportJob(next);
      } catch (err) {
        if (!stopped) setCompareExportError(err.message || 'Could not check export status');
      }
    };
    const timer = setInterval(poll, 2000);
    poll();
    return () => { stopped = true; clearInterval(timer); };
  }, [compareExportJob?.export_id, compareExportJob?.status]);

  // Keep leadLap valid as the selection changes.
  useEffect(() => {
    if (!visibleLapIdx.includes(leadLap)) {
      setLeadLap(visibleLapIdx[0] ?? null);
    }
  }, [visibleLapIdx, leadLap]);

  // ── Refs mirroring render state for the rAF follower-sync loop ─────────────
  const paneEls   = useRef(new Map());  // lapIndex → <video> element (split panes)
  const leadLapRef = useRef(leadLap);
  leadLapRef.current = leadLap;
  const stateRef  = useRef({});
  stateRef.current = {
    splitActive, syncMode, playMode, playing, effectiveSyncOffset,
    leadLap, visibleLapIdx, lapStarts, lapLookup,
  };

  // Register a split pane's element; keep videoRef pointing at the lead.
  const setPaneEl = useCallback((index, el) => {
    const m = paneEls.current;
    if (el) m.set(index, el); else m.delete(index);
    if (index === leadLapRef.current) videoRef.current = el ?? videoRef.current;
  }, []);

  // Non-split single element registration.
  const setSingleEl = useCallback((el) => { videoRef.current = el; }, []);

  // When the lead changes, repoint videoRef at the (already-mounted) lead element.
  useEffect(() => {
    if (splitActive) {
      const el = paneEls.current.get(leadLap);
      if (el) videoRef.current = el;
    }
  }, [leadLap, splitActive]);

  // ── Multi-driver sync plumbing ────────────────────────────────────────────
  const dualMeta = useMemo(() => Object.fromEntries(driverPanes.map(driver => [
    driver.key,
    {
      driver: driver.index + 1,
      src: driver.src,
      sessionId: driver.sid,
      offset: driver.offset,
      lapIndex: driver.focus,
      lapStart: driver.starts.get(driver.focus) ?? 0,
      lookup: driver.lookup.get(driver.focus),
    },
  ])), [driverPanes]);

  const dualEls = useRef({});
  const leadKey = `d${leadDriver}`;
  const leadFocus = dualMeta[leadKey]?.lapIndex;
  const leadKeyRef = useRef(leadKey);
  leadKeyRef.current = leadKey;
  // Offset of whatever element videoRef currently points at — used by seekTo and
  // the time-update cursor so they translate against the right driver's timeline.
  const leadOffsetRef = useRef(effectiveSyncOffset);
  leadOffsetRef.current = dualDriver ? (dualMeta[leadKey]?.offset ?? 0) : effectiveSyncOffset;
  const dualStateRef = useRef({});
  dualStateRef.current = { dualDriver, playing, leadKey, dualMeta };

  // Register a driver pane's element; keep videoRef pointing at the lead pane.
  const setDualEl = useCallback((key, el) => {
    dualEls.current[key] = el;
    if (key === leadKeyRef.current && el) videoRef.current = el;
  }, []);

  // Repoint videoRef when the REF driver changes, and refresh the transport from
  // the new lead element (the two videos have different durations, and the new
  // lead's onLoadedMetadata won't fire again once its metadata is already loaded).
  useEffect(() => {
    if (dualDriver) {
      const el = dualEls.current[leadKey];
      if (el) {
        videoRef.current = el;
        if (!isNaN(el.duration)) setDuration(el.duration);
        setCurrentT(el.currentTime);
      }
    }
  }, [leadKey, dualDriver]);

  // ── Expose seekTo via ref ─────────────────────────────────────────────────
  // seekTo accepts csv/lap time; translates to video time using the lead offset.
  useImperativeHandle(ref, () => ({
    seekTo(csvTime) {
      const v = videoRef.current;
      if (!v || csvTime == null) return;
      v.currentTime = csvTime + leadOffsetRef.current;
    },
  }), []);

  // ── Follower sync ──────────────────────────────────────────────────────────
  // Map the lead pane's current position onto each follower pane.
  const syncFollowers = useCallback(() => {
    const st = stateRef.current;
    if (!st.splitActive) return;
    // During a race the panes run free — don't seek them.
    if (st.playMode === 'race' && st.playing) return;
    const leadEl = paneEls.current.get(st.leadLap);
    if (!leadEl) return;

    const leadStart = st.lapStarts.get(st.leadLap) ?? 0;
    const timeInLap = leadEl.currentTime - st.effectiveSyncOffset - leadStart;

    // Distance along the lead lap (only when distance mode + lead trace present).
    const leadLk = st.lapLookup.get(st.leadLap);
    const dist = (st.syncMode === 'distance' && leadLk)
      ? lerpLookup(leadLk.byTime, 'time', 'dist', timeInLap)
      : null;

    for (const idx of st.visibleLapIdx) {
      if (idx === st.leadLap) continue;
      const el = paneEls.current.get(idx);
      if (!el) continue;

      const fStart = st.lapStarts.get(idx) ?? 0;
      const fLk    = st.lapLookup.get(idx);
      // Distance mapping when available; otherwise fall back to lap-relative time.
      const fTimeInLap = (dist != null && fLk)
        ? lerpLookup(fLk.byDist, 'dist', 'time', dist)
        : timeInLap;
      if (fTimeInLap == null) continue;

      const target = fStart + fTimeInLap + st.effectiveSyncOffset;
      // Seek exactly when paused (crisp scrubbing); allow a little slack while the
      // reference is playing to avoid thrashing.
      const threshold = st.playing ? 0.10 : 0.03;
      if (Math.abs(el.currentTime - target) > threshold) el.currentTime = target;
    }
  }, []);

  // rAF loop: whenever the lead's time changes, re-sync the followers. This
  // covers playback, scrubbing, hover-seeks and lap-chip jumps uniformly.
  useEffect(() => {
    if (!splitActive) return;
    let raf = 0;
    let lastLead = -1;
    const tick = () => {
      const leadEl = paneEls.current.get(leadLapRef.current);
      if (leadEl) {
        const lt = leadEl.currentTime;
        if (lt !== lastLead) { syncFollowers(); lastLead = lt; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [splitActive, syncFollowers]);

  // Re-sync immediately when the split config changes.
  useEffect(() => {
    if (splitActive) syncFollowers();
  }, [splitActive, syncMode, playMode, leadLap, lapLookup, effectiveSyncOffset, syncFollowers]);

  // On entering/leaving split, reset the transport (the active element changes,
  // so a stale `playing` flag would otherwise drive the wrong panes) and park the
  // new lead pane at its lap start for a sensible frame.
  useEffect(() => {
    setPlaying(false);
    for (const el of paneEls.current.values()) el.pause?.();
    if (!splitActive) return;
    const leadEl = paneEls.current.get(leadLap);
    const start  = lapStarts.get(leadLap);
    if (leadEl && start != null) leadEl.currentTime = start + effectiveSyncOffset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitActive]);

  // Enforce the desired play state across panes. The reference always follows the
  // transport; followers play only during a 'race' (otherwise they're seek-driven).
  useEffect(() => {
    if (!splitActive) return;
    for (const [idx, el] of paneEls.current) {
      const isLead    = idx === leadLap;
      const shouldPlay = playing && (isLead || playMode === 'race');
      if (shouldPlay) el.play?.().catch(() => {});
      else el.pause?.();
    }
  }, [playing, splitActive, playMode, leadLap]);

  // ── Two-driver follower sync ──────────────────────────────────────────────
  // Map the REF driver's current track position (distance) onto the other
  // driver's video via its own distance↔time lookup. Always distance-aligned.
  const syncDual = useCallback(() => {
    const st = dualStateRef.current;
    if (!st.dualDriver) return;
    const lead = st.dualMeta[st.leadKey];
    const leadEl = dualEls.current[st.leadKey];
    if (!lead || !leadEl) return;

    const timeInLap = leadEl.currentTime - lead.offset - lead.lapStart;
    // Distance along the lead lap → time-in-lap on the follower lap. Fall back to
    // raw lap-relative time when either trace hasn't loaded yet.
    const dist = lead.lookup ? lerpLookup(lead.lookup.byTime, 'time', 'dist', timeInLap) : null;
    const refAtDistance = (dist != null && lead.lookup)
      ? lerpLookup(lead.lookup.byDist, 'dist', 'time', dist)
      : timeInLap;
    const nextDeltas = { [st.leadKey]: 0 };
    const threshold = st.playing ? 0.10 : 0.03;
    for (const [followKey, foll] of Object.entries(st.dualMeta)) {
      if (followKey === st.leadKey) continue;
      const follEl = dualEls.current[followKey];
      if (!follEl) continue;
      const fTimeInLap = (dist != null && foll.lookup)
        ? lerpLookup(foll.lookup.byDist, 'dist', 'time', dist)
        : timeInLap;
      if (fTimeInLap == null) continue;
      nextDeltas[followKey] = fTimeInLap - refAtDistance;
      const target = foll.lapStart + fTimeInLap + foll.offset;
      if (Math.abs(follEl.currentTime - target) > threshold) follEl.currentTime = target;
    }
    const now = performance.now();
    if (now - lastDeltaUpdateRef.current >= 100) {
      lastDeltaUpdateRef.current = now;
      setLiveDeltas(nextDeltas);
    }
  }, []);

  // rAF loop: re-sync the follower whenever the REF driver's time changes.
  useEffect(() => {
    if (!dualDriver) return;
    let raf = 0;
    let lastLead = -1;
    const tick = () => {
      const leadEl = dualEls.current[leadKeyRef.current];
      if (leadEl) {
        const lt = leadEl.currentTime;
        if (lt !== lastLead) { syncDual(); lastLead = lt; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dualDriver, syncDual]);

  // Re-sync immediately when the two-driver config changes.
  useEffect(() => {
    if (dualDriver) syncDual();
  }, [dualDriver, leadKey, dualMeta, syncDual]);

  // On entering two-driver mode, swapping the REF driver, or changing the REF
  // driver's focus lap, reset the transport and park the REF pane at its lap start
  // (its currentTime is meaningless in the new lap's timeline). Changing the
  // *follower's* focus lap is handled by the immediate syncDual resync below.
  useEffect(() => {
    if (!dualDriver) return;
    setPlaying(false);
    const leadEl = dualEls.current[leadKey];
    const lead   = dualMeta[leadKey];
    if (leadEl && lead) {
      leadEl.pause?.();
      leadEl.currentTime = lead.lapStart + lead.offset;
    }
    for (const [key, element] of Object.entries(dualEls.current)) {
      if (key !== leadKey) element?.pause?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dualDriver, leadKey, leadFocus]);

  // Multi-driver play state: only REF plays; all followers are seek-driven.
  useEffect(() => {
    if (!dualDriver) return;
    for (const key of Object.keys(dualMeta)) {
      const el = dualEls.current[key];
      if (!el) continue;
      if (playing && key === leadKey) el.play?.().catch(() => {});
      else el.pause?.();
    }
  }, [playing, dualDriver, leadKey]);

  // Position the lead pane from a chart event. In split view we drive off the
  // event's track position (distFt) so every lap maps to *its own* point on
  // track; the rAF loop then cascades to the follower panes. The raw csv time is
  // only a fallback (single-view, or when the lead lap has no trace loaded).
  const seekFromChart = useCallback((distFt, csvTime) => {
    const st = stateRef.current;
    // Two-driver: drive the REF pane by track position; rAF cascades to the other.
    const dst = dualStateRef.current;
    if (dst.dualDriver) {
      const lead   = dst.dualMeta[dst.leadKey];
      const leadEl = dualEls.current[dst.leadKey];
      if (!lead || !leadEl) return;
      if (lead.lookup && distFt != null) {
        const t = lerpLookup(lead.lookup.byDist, 'dist', 'time', distFt / M_TO_FT);
        if (t != null) { leadEl.currentTime = lead.lapStart + t + lead.offset; return; }
      }
      if (csvTime != null) leadEl.currentTime = csvTime + lead.offset;
      return;
    }
    const v = paneEls.current.get(st.leadLap) ?? videoRef.current;
    if (!v) return;
    if (st.splitActive) {
      const leadLk    = st.lapLookup.get(st.leadLap);
      const leadStart = st.lapStarts.get(st.leadLap) ?? 0;
      if (leadLk && distFt != null) {
        const t = lerpLookup(leadLk.byDist, 'dist', 'time', distFt / M_TO_FT);
        if (t != null) { v.currentTime = leadStart + t + st.effectiveSyncOffset; return; }
      }
    }
    if (csvTime != null) v.currentTime = csvTime + st.effectiveSyncOffset;
  }, []);

  // ── Listen for chart-hover → seek ─────────────────────────────────────────
  // Chart events carry csv time and track position (distFt).
  useEffect(() => {
    const handler = (e) => {
      if (ignoreHoverRef.current) return;
      if (e.detail?.fromVideo) return;  // don't seek in response to our own playback events
      const csvTime = e.detail?.videoTime;  // field named videoTime for back-compat
      if (csvTime == null) return;
      const v = videoRef.current;
      if (!v) return;
      if (!shouldSeekFromChartHover({
        fromKeyboard: e.detail?.fromKeyboard,
        hoverEnabled: syncOnMouseMoveRef.current,
        playing: !v.paused,
      })) return;
      seekFromChart(e.detail?.distFt, csvTime);
    };
    window.addEventListener('trackapp:chart-hover', handler);
    return () => window.removeEventListener('trackapp:chart-hover', handler);
  }, [seekFromChart]);

  // ── Listen for chart-click → seek (always, regardless of syncOnMouseMove) ──
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.fromVideo) return;
      const csvTime = e.detail?.videoTime;
      if (csvTime == null) return;
      if (!videoRef.current) return;
      seekFromChart(e.detail?.distFt, csvTime);
    };
    window.addEventListener('trackapp:chart-click', handler);
    return () => window.removeEventListener('trackapp:chart-click', handler);
  }, [seekFromChart]);

  // ── Video event handlers (attached to the lead / single element) ───────────
  // The video is the transport authority while it is open. Along with its csv
  // clock, publish track distance so Lap3D can follow whichever lap/driver is
  // currently REF without relying on a Speed panel to translate time first.
  const emitVideoTime = useCallback((playingOverride) => {
    const v = videoRef.current;
    if (!v) return;
    const videoTime = v.currentTime - leadOffsetRef.current;
    const dst = dualStateRef.current;
    let candidates;
    if (dst.dualDriver) {
      const lead = dst.dualMeta[dst.leadKey];
      if (!lead) return;
      candidates = [{ startTime: lead.lapStart, byTime: lead.lookup?.byTime }];
    } else {
      const st = stateRef.current;
      const indexes = st.splitActive ? [st.leadLap] : st.visibleLapIdx;
      candidates = indexes.map((index) => ({
        startTime: st.lapStarts.get(index),
        byTime: st.lapLookup.get(index)?.byTime,
      }));
    }
    const distM = distanceMetersAtVideoTime(videoTime, candidates);
    window.dispatchEvent(new CustomEvent('trackapp:video-time', {
      detail: {
        videoTime,
        distFt: Number.isFinite(distM) ? distM * M_TO_FT : null,
        playing: playingOverride ?? !v.paused,
        playbackRate: v.playbackRate,
        source: 'video-panel',
      },
    }));
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentT(v.currentTime);
    emitVideoTime();
  }, [emitVideoTime]);

  const handleLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
    // Only one panel is mounted at a time on mobile: resume at the last
    // cross-panel cursor position (where the user scrubbed a trace).
    const last = lastCursor();
    if (last?.videoTime != null) seekFromChart(last.distFt, last.videoTime);
  }, [seekFromChart]);

  const handlePlay = useCallback(() => {
    setPlaying(true);
    emitVideoTime(true);
  }, [emitVideoTime]);
  const handlePause = useCallback(() => {
    setPlaying(false);
    emitVideoTime(false);
  }, [emitVideoTime]);
  // `paused` remains false while media is buffering, so relay the media's
  // actual ability to advance rather than letting Lap3D run ahead of a stall.
  const handleWaiting = useCallback(() => emitVideoTime(false), [emitVideoTime]);
  const handlePlaying = useCallback(() => emitVideoTime(true), [emitVideoTime]);

  // If the panel disappears during playback, do not leave an open 3D panel
  // running with a transport that no longer exists.
  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent('trackapp:video-time', {
      detail: { playing: false, source: 'video-panel' },
    }));
  }, []);

  const leadHandlers = {
    onTimeUpdate:     handleTimeUpdate,
    onLoadedMetadata: handleLoadedMetadata,
    onPlay:           handlePlay,
    onPause:          handlePause,
    onPlaying:        handlePlaying,
    onWaiting:        handleWaiting,
    onEnded:          handlePause,
  };

  // ── Progress bar interaction ───────────────────────────────────────────────
  const getTimeFromEvent = useCallback((e) => {
    const bar = progressRef.current;
    if (!bar || !duration) return null;
    const rect  = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const handleBarDown = useCallback((e) => {
    e.preventDefault();
    ignoreHoverRef.current = true;
    setSeeking(true);
    const t = getTimeFromEvent(e);
    if (t != null && videoRef.current) videoRef.current.currentTime = t;
  }, [getTimeFromEvent]);

  useEffect(() => {
    if (!seeking) return;
    const onMove = (e) => {
      const t = getTimeFromEvent(e);
      if (t != null && videoRef.current) videoRef.current.currentTime = t;
    };
    const onUp = () => {
      setSeeking(false);
      // Re-enable hover sync after a short delay
      setTimeout(() => { ignoreHoverRef.current = false; }, 500);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',  onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',  onUp);
    };
  }, [seeking, getTimeFromEvent]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  }, []);

  const progress = duration ? currentT / duration : 0;

  const clippableLaps = laps.filter(l => l.index !== -1);

  const handleClipOpen = useCallback(() => {
    setClipOpen(v => {
      if (!v && clipLapIdx === null && clippableLaps.length > 0) {
        setClipLapIdx(clippableLaps[0].index);
        setClipEndIdx(clippableLaps[0].index);
      }
      return !v;
    });
    setClipError(null);
  }, [clipLapIdx, clippableLaps]);

  const handleClipFromChange = useCallback((idx) => {
    setClipLapIdx(idx);
    // Moving the start past the end drags the end along: the span stays
    // contiguous and never inverts.
    setClipEndIdx(end => (end === null || end < idx) ? idx : end);
  }, []);

  // "To" only offers laps at or after "From" - the UI has no way to express a
  // gap, so L1, L2, L5 is not a selection that can be made.
  const clipEndChoices = useMemo(
    () => clipLapIdx === null ? [] : clippableLaps.filter(l => l.index >= clipLapIdx),
    [clippableLaps, clipLapIdx],
  );

  // What the download will contain: the video runs from the start of the
  // first lap to the end of the last, so any pit time inside the span counts.
  const clipEstimate = useMemo(() => {
    if (clipLapIdx === null) return null;
    const first = laps.find(l => l.index === clipLapIdx);
    const last = laps.find(l => l.index === (clipEndIdx ?? clipLapIdx));
    if (!first || !last) return null;
    const span = Math.max(0, last.end_time - first.start_time);
    return { lapCount: last.index - first.index + 1, seconds: span + clipPre + clipPost };
  }, [laps, clipLapIdx, clipEndIdx, clipPre, clipPost]);

  const handleCreateClip = useCallback(async () => {
    if (clipLapIdx === null) return;
    setClipLoading(true);
    setClipError(null);
    try {
      const job = await submitLapClip(sessionId, {
        lapIndex: clipLapIdx,
        endLapIndex: clipEndIdx ?? clipLapIdx,
        preSeconds: clipPre,
        postSeconds: clipPost,
      });
      setClipJob(job);
    } catch (err) {
      setClipError(err.message || 'Failed to create clip');
    } finally {
      setClipLoading(false);
    }
  }, [sessionId, clipLapIdx, clipEndIdx, clipPre, clipPost]);

  // Poll the clip job while it runs; the encode reports its own progress.
  useEffect(() => {
    const clipId = clipJob?.clip_id;
    if (!clipId || clipJob.download_ready || VIDEO_COMPARISON_TERMINAL.has(clipJob.status)) return;
    let stopped = false;
    const poll = async () => {
      try {
        const next = await getLapClipStatus(sessionId, clipId);
        if (!stopped) setClipJob(next);
      } catch (err) {
        if (!stopped) setClipError(err.message || 'Could not check clip status');
      }
    };
    const timer = setInterval(poll, 1000);
    poll();
    return () => { stopped = true; clearInterval(timer); };
  }, [sessionId, clipJob?.clip_id, clipJob?.status, clipJob?.download_ready]);

  // A new session means a new video; a job from the last one is meaningless here.
  useEffect(() => { setClipJob(null); setClipError(null); }, [sessionId]);

  const handleDownloadClip = useCallback(async () => {
    if (!clipJob?.clip_id) return;
    setClipLoading(true);
    setClipError(null);
    try {
      const { blob, filename } = await downloadLapClip(sessionId, clipJob.clip_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setClipJob(null);
      setClipOpen(false);
    } catch (err) {
      setClipError(err.message || 'Failed to download clip');
    } finally {
      setClipLoading(false);
    }
  }, [sessionId, clipJob?.clip_id]);

  // Traces are fetched lazily and land on props, so the capture reads them
  // through a ref rather than closing over whatever was loaded when it started.
  const driverInputsRef = useRef(driverInputs);
  driverInputsRef.current = driverInputs;
  const lapPointsFor3D = useCallback(
    (sid, lapIndex) => driverInputsRef.current
      .find(driver => driver.sessionId === sid)?.lapTraces?.[lapIndex] || null,
    [],
  );

  /**
   * Render the ghost race and stage it, resolving with a clip id — or null if
   * the user backed out, in which case the export is abandoned too rather than
   * quietly submitted without the tile they asked for.
   */
  const renderComparison3D = useCallback((panes) => new Promise((resolve) => {
    capture3DDone.current = resolve;
    setCapture3D({
      laps: comparison3DLaps(panes),
      refIndex: panes.length === 1 ? 0 : leadDriver - 1,
      // Rendered straight into the cell it lands in, so the server never
      // resamples it and a solo export fills its half rather than sitting
      // letterboxed in the middle of it.
      size: comparison3dCellSize(panes.length),
    });
  }), [leadDriver, comparison3DLaps]);

  const finishCapture3D = useCallback((clipId) => {
    setCapture3D(null);
    const resolve = capture3DDone.current;
    capture3DDone.current = null;
    resolve?.(clipId);
  }, []);

  const handleCreateComparisonExport = useCallback(async () => {
    if (!canCompareExport || driverPanes.some(driver => driver.focus == null)) return;
    const panes = driverPanes;
    setCompareExportBusy(true);
    setCompareExportError(null);
    try {
      let threeDClipId = null;
      if (use3D) {
        threeDClipId = await renderComparison3D(panes);
        if (!threeDClipId) return;
      }
      const job = await createVideoComparisonExport(buildVideoComparisonExportRequest({
        drivers: panes.map(driver => ({
          sessionId: driver.sid,
          lapIndex: driver.focus,
          videoOffset: driver.offset,
        })),
        referenceDriver: leadDriver,
        playbackMode: soloWith3D ? 'distance' : compareExportMode,
        preSeconds: compareExportPre,
        postSeconds: compareExportPost,
        threeDClipId,
      }));
      setCompareExportJob(job);
      if (compareExportStorageKey) localStorage.setItem(compareExportStorageKey, job.export_id);
    } catch (err) {
      setCompareExportError(err.message || 'Failed to start comparison export');
    } finally {
      setCompareExportBusy(false);
    }
  }, [dualDriver, driverPanes, leadDriver, compareExportMode, compareExportPre,
      compareExportPost, compareExportStorageKey, use3D, canCompareExport,
      soloWith3D, renderComparison3D]);

  const handleDownloadComparisonExport = useCallback(async () => {
    if (!compareExportJob?.export_id) return;
    setCompareExportBusy(true);
    setCompareExportError(null);
    try {
      const { blob, filename } = await downloadVideoComparisonExport(compareExportJob.export_id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setCompareExportError(err.message || 'Failed to download comparison export');
    } finally {
      setCompareExportBusy(false);
    }
  }, [compareExportJob?.export_id]);

  const handleCancelComparisonExport = useCallback(async () => {
    if (!compareExportJob?.export_id) return;
    setCompareExportBusy(true);
    try {
      await cancelVideoComparisonExport(compareExportJob.export_id);
      if (compareExportStorageKey) localStorage.removeItem(compareExportStorageKey);
      setCompareExportJob(null);
      setCompareExportError(null);
    } catch (err) {
      setCompareExportError(err.message || 'Failed to cancel comparison export');
    } finally {
      setCompareExportBusy(false);
    }
  }, [compareExportJob?.export_id, compareExportStorageKey]);

  const handleDeleteVideo = useCallback(async () => {
    if (!window.confirm('Remove the video from this session? This cannot be undone.')) return;
    try {
      await deleteSessionVideo(sessionId);
      onVideoDeleted?.();
    } catch (err) {
      alert(`Failed to delete video: ${err.message}`);
    }
  }, [sessionId, onVideoDeleted]);

  const csvTime = currentT - effectiveSyncOffset;
  const activeLapIndex = visibleLaps.find(
    l => csvTime >= l.start_time && csvTime < l.end_time
  )?.index ?? null;

  const persistSplit = (val) => {
    setSplit(val);
    localStorage.setItem('videoPanel.split', String(val));
  };
  const toggleSyncMode = () => {
    const m = syncMode === 'distance' ? 'time' : 'distance';
    setSyncMode(m);
    localStorage.setItem('videoPanel.splitSyncMode', m);
  };
  const togglePlayMode = () => {
    const m = playMode === 'sync' ? 'race' : 'sync';
    setPlayMode(m);
    localStorage.setItem('videoPanel.splitPlayMode', m);
  };

  const lapLabelOf = (lap) => `L${timedLapDisplayNum.get(lap.index) ?? lap.lap_number}`;
  const clipLapOptionLabel = (lap) =>
    `${lapLabelOf(lap)}${lap.is_outlap ? ' (out)' : lap.is_inlap ? ' (in)' : ''} — ${lap.lap_time_str}`;
  const lapLabelIn = (lap, timed) => `L${timed.get(lap.index) ?? lap.lap_number}`;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.time}>{fmt(currentT)} / {fmt(duration)}</span>
        {onVideoDeleted && (
          <button
            className={styles.dockBtn}
            onClick={handleDeleteVideo}
            title="Remove video from session"
          >del vid</button>
        )}
        {downloadUrl && (
          <a
            href={downloadUrl}
            className={styles.dockBtn}
            title="Download overlay video"
            style={{ textDecoration: 'none' }}
          >⬇</a>
        )}
        {clippableLaps.length > 0 && (
          <button
            className={`${styles.dockBtn} ${clipOpen ? styles.dockBtnActive : ''}`}
            onClick={handleClipOpen}
            title="Create lap clip"
          >✂</button>
        )}
        {canCompareExport && (
          <button
            className={`${styles.dockBtn} ${compareExportOpen ? styles.dockBtnActive : ''}`}
            onClick={() => {
              setCompareExportOpen(value => !value);
              setCompareExportError(null);
            }}
            title={compareExportLabel}
            aria-label={compareExportLabel}
          >⇆⬇</button>
        )}
        {!dualDriver && visibleLaps.length >= 2 && (
          <label className={styles.syncCheck} title="Show one video pane per selected lap, synced to the same point on track">
            <input
              type="checkbox"
              checked={split}
              onChange={e => persistSplit(e.target.checked)}
            />
            split
          </label>
        )}
        {splitActive && (
          <button
            className={`${styles.dockBtn} ${styles.modeToggle}`}
            onClick={toggleSyncMode}
            title={syncMode === 'distance'
              ? 'Scrub/hover aligns panes by track position (distance). Click for lap-relative time.'
              : 'Scrub/hover aligns panes by lap-relative time. Click for track position (distance).'}
          >
            {syncMode === 'distance' ? '⇄ distance' : '⏱ time'}
          </button>
        )}
        {splitActive && (
          <button
            className={`${styles.dockBtn} ${styles.modeToggle}`}
            onClick={togglePlayMode}
            title={playMode === 'sync'
              ? 'Play: the ★ REF lap plays and the others stay locked to the same point on track. Click for a ghost race.'
              : 'Play: every lap plays its own footage in real time and they drift apart (ghost race). Click to keep them aligned.'}
          >
            {playMode === 'sync' ? '▶ together' : '▶ race'}
          </button>
        )}
        <label className={styles.syncCheck}>
          <input
            type="checkbox"
            checked={syncOnMouseMove}
            onChange={e => {
              syncOnMouseMoveRef.current = e.target.checked;
              setSyncOnMouseMove(e.target.checked);
              localStorage.setItem('videoPanel.syncOnMouseMove', e.target.checked);
            }}
          />
          hover sync
        </label>
        <div className={styles.offsetsWrap}>
          {(dualDriver ? driverPanes : driverPanes.slice(0, 1)).map((driver, index) => (
            <div key={driver.sessionId} className={styles.trimWrap}>
              <span className={styles.trimLabel}>{dualDriver ? `${driver.name} offset` : 'offset adj'}</span>
              <input
                className={styles.trimInput}
                type="number"
                step="0.1"
                value={index === 0 ? trim : driver.offset}
                onChange={e => {
                  const value = parseFloat(e.target.value) || 0;
                  if (index === 0) setTrim(value);
                  else setDriverTrims(previous => ({ ...previous, [driver.sessionId]: value }));
                }}
                title={`Fine-tune ${driver.name}'s video sync independently.`}
              />
              <span className={styles.trimLabel}>s</span>
            </div>
          ))}
        </div>
        <HelpButton panel="videopanel" page="sessions" className={styles.dockBtn} />
      </div>

      {clipOpen && (
        <div className={styles.clipPanel}>
          <div className={styles.clipRow}>
            <label className={styles.clipLabel}>From</label>
            <select
              className={styles.clipSelect}
              value={clipLapIdx ?? ''}
              onChange={e => handleClipFromChange(Number(e.target.value))}
            >
              {clippableLaps.map(lap => (
                <option key={lap.index} value={lap.index}>{clipLapOptionLabel(lap)}</option>
              ))}
            </select>
          </div>
          <div className={styles.clipRow}>
            <label className={styles.clipLabel}>To</label>
            <select
              className={styles.clipSelect}
              value={clipEndIdx ?? clipLapIdx ?? ''}
              onChange={e => setClipEndIdx(Number(e.target.value))}
            >
              {clipEndChoices.map(lap => (
                <option key={lap.index} value={lap.index}>{clipLapOptionLabel(lap)}</option>
              ))}
            </select>
          </div>
          <div className={styles.clipRow}>
            <label className={styles.clipLabel}>Pre</label>
            <input
              className={styles.clipNumInput}
              type="number"
              min={0}
              max={120}
              step={1}
              value={clipPre}
              onChange={e => setClipPre(Math.max(0, Number(e.target.value)))}
            />
            <span className={styles.clipUnit}>s</span>
            <label className={styles.clipLabel} style={{ marginLeft: 8 }}>Post</label>
            <input
              className={styles.clipNumInput}
              type="number"
              min={0}
              max={120}
              step={1}
              value={clipPost}
              onChange={e => setClipPost(Math.max(0, Number(e.target.value)))}
            />
            <span className={styles.clipUnit}>s</span>
          </div>
          {clipEstimate && (
            <div className={styles.clipEstimate}>
              {clipEstimate.lapCount} {clipEstimate.lapCount === 1 ? 'lap' : 'laps'}
              {' · ~'}{fmt(clipEstimate.seconds)}
            </div>
          )}
          {clipError && <div className={styles.clipError}>{clipError}</div>}
          {!clipJob && (
            <button
              className={styles.clipBtn}
              onClick={handleCreateClip}
              disabled={clipLoading || clipLapIdx === null}
            >
              {clipLoading ? 'Submitting…' : '⬇ Create Clip'}
            </button>
          )}
          {clipJob && (
            <div className={styles.compareExportProgress}>
              <div>{clipJob.stage_message || clipJob.status}</div>
              <div className={styles.compareProgressTrack}>
                <span style={{ width: `${clipJob.progress_pct || 0}%` }} />
              </div>
              <div>{clipJob.progress_pct || 0}%</div>
              {clipJob.download_ready ? (
                <div className={styles.compareExportActions}>
                  <button className={styles.clipBtn} onClick={handleDownloadClip} disabled={clipLoading}>
                    {clipLoading ? 'Preparing…' : '⬇ Download Clip'}
                  </button>
                  <button className={styles.compareCancelBtn} onClick={() => setClipJob(null)} disabled={clipLoading}>
                    New clip
                  </button>
                </div>
              ) : VIDEO_COMPARISON_TERMINAL.has(clipJob.status) ? (
                <>
                  {clipJob.error && <div className={styles.clipError}>{clipJob.error}</div>}
                  <button className={styles.compareCancelBtn} onClick={() => setClipJob(null)}>
                    Dismiss
                  </button>
                </>
              ) : null}
            </div>
          )}
        </div>
      )}

      {capture3D && (
        <Suspense fallback={null}>
          <Comparison3DCapture
            laps={capture3D.laps}
            refIndex={capture3D.refIndex}
            size={capture3D.size}
            getLapPoints={lapPointsFor3D}
            onDone={finishCapture3D}
            onCancel={() => finishCapture3D(null)}
          />
        </Suspense>
      )}

      {canCompareExport && compareExportOpen && (
        <div className={styles.clipPanel}>
          <div className={styles.compareExportTitle}>Side-by-side MP4</div>
          <div className={styles.compareExportDrivers}>
            {driverPanes.map((driver, index) => (
              <span key={driver.sessionId}>
                {index + 1}. {driver.name} · {driver.visible.find(lap => lap.index === driver.focus)?.lap_time_str || '—'}
                {leadDriver === index + 1 ? ' · REF' : ''}
              </span>
            ))}
          </div>
          {!compareExportJob && (
            <>
              {/* Nothing to choose with one video: the lone pane plays at
                  natural speed either way, and the tile's reference car IS
                  that video's lap, so both modes ask where the same driver is
                  at the same moment. They differ only by the 3D lap's anchor
                  offset, which `distance` corrects for and `race` does not. */}
              {!soloWith3D && (
                <>
                  <label className={styles.compareExportMode}>
                    <input
                      type="radio" name="comparison-export-mode" value="distance"
                      checked={compareExportMode === 'distance'}
                      onChange={() => setCompareExportMode('distance')}
                    />
                    <span>
                      <strong>Same position</strong>
                      <small>Keep every video at the same place on track.</small>
                    </span>
                  </label>
                  <label className={styles.compareExportMode}>
                    <input
                      type="radio" name="comparison-export-mode" value="race"
                      checked={compareExportMode === 'race'}
                      onChange={() => setCompareExportMode('race')}
                    />
                    <span>
                      <strong>Race from line</strong>
                      <small>Run every lap at natural speed from start/finish.</small>
                    </span>
                  </label>
                </>
              )}
              <div className={styles.clipRow}>
                <label className={styles.clipLabel}>Pre</label>
                <input
                  className={styles.clipNumInput}
                  type="number"
                  min={0}
                  max={120}
                  step={1}
                  value={compareExportPre}
                  onChange={e => setCompareExportPre(Math.min(120, Math.max(0, Number(e.target.value))))}
                />
                <span className={styles.clipUnit}>s</span>
                <label className={styles.clipLabel} style={{ marginLeft: 8 }}>Post</label>
                <input
                  className={styles.clipNumInput}
                  type="number"
                  min={0}
                  max={120}
                  step={1}
                  value={compareExportPost}
                  onChange={e => setCompareExportPost(Math.min(120, Math.max(0, Number(e.target.value))))}
                />
                <span className={styles.clipUnit}>s</span>
              </div>
              <label className={styles.compareExport3D} title={threeDNote}>
                <input
                  type="checkbox"
                  checked={use3D}
                  disabled={!canRender3D || soloWith3D}
                  onChange={e => setCompareExport3D(e.target.checked)}
                />
                <span>
                  <strong>Include 3D view</strong>
                  <small>{threeDNote}</small>
                </span>
              </label>
              <div className={styles.compareExportNote}>
                {soloWith3D
                  ? 'Audio comes from the video; the 3D view follows its position on track.'
                  : `Audio and timing reference: ${driverPanes[leadDriver - 1]?.name} (REF)`}
              </div>
              <button
                className={styles.clipBtn}
                onClick={handleCreateComparisonExport}
                disabled={compareExportBusy || driverPanes.some(driver => driver.focus == null)}
              >{compareExportBusy ? 'Submitting…' : 'Create Comparison MP4'}</button>
            </>
          )}
          {compareExportJob && (
            <div className={styles.compareExportProgress}>
              <div>{compareExportJob.stage_message || compareExportJob.status}</div>
              <div className={styles.compareProgressTrack}>
                <span style={{ width: `${compareExportJob.progress_pct || 0}%` }} />
              </div>
              <div>{compareExportJob.progress_pct || 0}%</div>
              {compareExportJob.download_ready ? (
                <div className={styles.compareExportActions}>
                  <button className={styles.clipBtn} onClick={handleDownloadComparisonExport} disabled={compareExportBusy}>
                    {compareExportBusy ? 'Preparing…' : '⬇ Download Comparison MP4'}
                  </button>
                  <button className={styles.compareCancelBtn} onClick={handleCancelComparisonExport} disabled={compareExportBusy}>
                    New comparison
                  </button>
                </div>
              ) : VIDEO_COMPARISON_TERMINAL.has(compareExportJob.status) ? (
                <button className={styles.compareCancelBtn} onClick={handleCancelComparisonExport} disabled={compareExportBusy}>
                  Dismiss
                </button>
              ) : (
                <button className={styles.compareCancelBtn} onClick={handleCancelComparisonExport} disabled={compareExportBusy}>
                  Cancel
                </button>
              )}
            </div>
          )}
          {(compareExportError || compareExportJob?.error) && (
            <div className={styles.clipError}>{compareExportError || compareExportJob.error}</div>
          )}
        </div>
      )}

      {/* Lap switcher (single-view only — split panes act as their own selector) */}
      {!dualDriver && !splitActive && visibleLaps.length >= 2 && (
        <div className={styles.lapSwitcher}>
          {visibleLaps.map(lap => {
            const color = lapColors[lap.index] ?? '#888';
            const isActive = lap.index === activeLapIndex;
            return (
              <button
                key={lap.index}
                className={`${styles.lapChip} ${isActive ? styles.lapChipActive : ''}`}
                style={{ '--chip-color': color }}
                onClick={() => {
                  const v = videoRef.current;
                  if (v) v.currentTime = lap.start_time + effectiveSyncOffset;
                }}
                title={`Seek to Lap ${lapLabelOf(lap)} start`}
              >
                <span className={styles.lapChipNum}>{lapLabelOf(lap)}</span>
                <span className={styles.lapChipTime}>{lap.lap_time_str}</span>
              </button>
            );
          })}
        </div>
      )}

      {dualDriver ? (
        <div className={styles.splitWrap} data-multi-count={driverPanes.length}>
          {driverPanes.map(dp => {
            const isLead = dp.key === leadKey;
            const color  = dp.colors[dp.focus] ?? '#888';
            const focusLapObj = dp.visible.find(l => l.index === dp.focus);
            return (
              <div
                key={dp.key}
                className={`${styles.splitPane} ${isLead ? styles.splitPaneLead : ''}`}
                style={{ '--pane-color': color }}
                onClick={() => setLeadDriver(dp.index + 1)}
                title={isLead
                  ? `${dp.name} is the reference — plays and drives the speed-trace cursor. Click the other pane to swap.`
                  : `Click to make ${dp.name} the reference`}
              >
                <div className={styles.splitLabel}>
                  <span className={styles.splitLabelNum}>{dp.name}</span>
                  {focusLapObj && (
                    <span className={styles.splitLabelTime}>{focusLapObj.lap_time_str}</span>
                  )}
                  {isLead && <span className={styles.splitLeadTag}>★ REF</span>}
                  {!isLead && Number.isFinite(liveDeltas[dp.key]) && (
                    <span className={styles.splitDelta}>{liveDeltas[dp.key] >= 0 ? '+' : ''}{liveDeltas[dp.key].toFixed(3)}s</span>
                  )}
                </div>
                {/* Per-driver focus-lap switcher (only when that driver has 2+ laps) */}
                {dp.visible.length >= 2 && (
                  <div className={styles.dualLapSwitcher} onClick={e => e.stopPropagation()}>
                    {dp.visible.map(lap => (
                      <button
                        key={lap.index}
                        className={`${styles.lapChip} ${lap.index === dp.focus ? styles.lapChipActive : ''}`}
                        style={{ '--chip-color': dp.colors[lap.index] ?? '#888' }}
                        onClick={() => dp.setFocus(lap.index)}
                        title={`Show ${dp.name} ${lapLabelIn(lap, dp.timed)} (${lap.lap_time_str})`}
                      >
                        <span className={styles.lapChipNum}>{lapLabelIn(lap, dp.timed)}</span>
                      </button>
                    ))}
                  </div>
                )}
                <LapVideo
                  src={dp.src}
                  sessionId={dp.sid}
                  className={styles.splitVideo}
                  onEl={el => setDualEl(dp.key, el)}
                  handlers={isLead ? leadHandlers : undefined}
                  splitPane
                />
              </div>
            );
          })}
          {driverPanes.length === 3 && (
            <div className={styles.multiSummary}>
              <strong>LIVE ORDER</strong>
              {[...driverPanes]
                .sort((a, b) => (liveDeltas[a.key] ?? 0) - (liveDeltas[b.key] ?? 0))
                .map((driver, index) => (
                  <div key={driver.sessionId}>
                    <span>{index + 1}. {driver.name}</span>
                    <span>{driver.key === leadKey ? 'REF' : `${(liveDeltas[driver.key] ?? 0) >= 0 ? '+' : ''}${(liveDeltas[driver.key] ?? 0).toFixed(3)}s`}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      ) : splitActive ? (
        <div className={styles.splitWrap} data-count={visibleLaps.length}>
          {visibleLaps.map(lap => {
            const color  = lapColors[lap.index] ?? '#888';
            const isLead = lap.index === leadLap;
            return (
              <div
                key={lap.index}
                className={`${styles.splitPane} ${isLead ? styles.splitPaneLead : ''}`}
                style={{ '--pane-color': color }}
                onClick={() => setLeadLap(lap.index)}
                title={isLead
                  ? 'Reference lap — plays in real time and drives the speed-trace cursor. Click another pane to change.'
                  : `Click to make ${lapLabelOf(lap)} the reference lap`}
              >
                <div className={styles.splitLabel}>
                  <span className={styles.splitLabelNum}>{lapLabelOf(lap)}</span>
                  <span className={styles.splitLabelTime}>{lap.lap_time_str}</span>
                  {isLead && <span className={styles.splitLeadTag}>★ REF</span>}
                </div>
                <LapVideo
                  src={videoSrc}
                  sessionId={sessionId}
                  className={styles.splitVideo}
                  onEl={el => setPaneEl(lap.index, el)}
                  handlers={isLead ? leadHandlers : undefined}
                  splitPane
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.videoWrap}>
          <LapVideo
            src={videoSrc}
            sessionId={sessionId}
            className={styles.video}
            onEl={setSingleEl}
            handlers={leadHandlers}
          />
        </div>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        <button className={styles.playBtn} onClick={togglePlay}>
          {playing ? '⏸' : '▶'}
        </button>

        {/* Scrub bar */}
        <div
          ref={progressRef}
          className={`${styles.progressBar} ${seeking ? styles.seeking : ''}`}
          onMouseDown={handleBarDown}
        >
          <div className={styles.progressFill} style={{ width: `${progress * 100}%` }} />
          <div className={styles.progressThumb} style={{ left: `${progress * 100}%` }} />
        </div>
      </div>
    </div>
  );
});

export default VideoPanel;
